import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SearchNoResults } from "../SearchNoResults";
import {
	BusyIndicator,
	NoResults,
	ScrollableVirtualList,
	ScrollableVirtualListHandle
} from "client/Components";
import {
	RemoteDataSourceState,
	createRemoteDataSource,
	useUserTimezone,
	useValueEqualMemo
} from "client/Hooks";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	IntegrationFlowGlobalSearchResultItem,
	searchIntegration
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { TimeZone } from "client/DateAndTime/timezones";
import { tryParseErrorResult } from "client/Features/Settings/IntegrationBuilder/common";
import { never } from "client_legacy/ndaw/errors";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { ScopeLayout } from "../ScopeLayout";
import { IntegrationFlowItemView } from "../Items/IntegrationFlowItemView";
import { Guid } from "client_legacy/ndaw/guid";
import { AuthenticateIntegrationFlowResultKind, useTryAuthenticateIntegrationFlow } from "client/Features/Layout/TitleBar/GlobalSearch/useAuthenticateIntegrationFlow";
import {
	IntegrationFlowAuthenticationIsRequiredModel,
	IntegrationFlowMultipleAuthenticationsAreRequiredModel
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";

export type IntegrationFlowSearchProps = {
	searchTerm: VersionedValue<string>;
	integrationId: number;
	integrationName?: string;
};

export const IntegrationFlowSearch = (props: IntegrationFlowSearchProps): ReactElement => {
	const {
		searchTerm,
		integrationId,
		integrationName
	} = props;

	const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
	const [authenticationState, setAuthenticationState] = useState<AuthenticationState | null>(null);

	const [authenticationDialogs, authenticate] = useTryAuthenticateIntegrationFlow();
	const listHandle: RefObject<ScrollableVirtualListHandle<ResultItem> | null> = useRef<ScrollableVirtualListHandle<ResultItem>>(null);

	const partialDataSourceOptions: { searchTerm: string; } = useValueEqualMemo(
		() => ({
			searchTerm: searchTerm
				.value
		}),
		[
			searchTerm.value
		]
	);

	const userTimezone: TimeZone = useUserTimezone();

	const errorHandler = useCallback(
		(e: unknown): void => {
			const authenticationRequiredError: TryResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel> = tryParseErrorResult<IntegrationFlowMultipleAuthenticationsAreRequiredModel>(e);

			if (!authenticationRequiredError.wasSuccessful) {
				return;
			}

			const {
				authentications: missingAuthentications
			} = authenticationRequiredError.result;

			setAuthenticationState({
				missingAuthentications,
				wasInvoked: false
			});
		},
		[]
	);

	const {
		currentItems,
		reload,
		getLoadingState
	} = useDataSource({
		searchTerm: searchTerm.value,
		filtering: {
			integrationId
		},
		globalContext: {
			searchVersion: searchTerm.version
		},
		onError: errorHandler
	});

	useEffect(
		(): void => {
			hasImplicitDependencyOn(partialDataSourceOptions);

			listHandle.current
				?.scrollToTop();
		},
		[partialDataSourceOptions]
	);

	const integrationFlowScope = useMemo(
		(): GlobalSearchScopeIdentifier => {
			return {
				kind: GlobalSearchScopeKind.IntegrationFlow,
				integrationId
			};
		},
		[integrationId]
	);

	const authenticateConfiguration = useMemo(
		(): { onRetry: () => void; retryText: string; } | undefined => {
			if (isNil(authenticationState)) {
				return undefined;
			}

			if (authenticationState.wasInvoked) {
				return {
					onRetry: (): void => {
						setAuthenticationState(null);
						reload();
					},
					retryText: localize("_Finish")
				};
			}

			return {
				onRetry: async (): Promise<void> => {
					if (isAuthenticating) {
						return;
					}

					const {
						missingAuthentications
					} = authenticationState;

					const result: TryResult<AuthenticateIntegrationFlowResultKind> = await authenticate(
						integrationName,
						missingAuthentications,
						setIsAuthenticating
					);

					if (!result.wasSuccessful) {
						return;
					}

					switch (result.result) {
						case "completed":
							setAuthenticationState(null);
							reload();
							break;
						case "wasInvoked":
							setAuthenticationState(x => ({
								...ensureNotNil(x),
								wasInvoked: true
							}));
							break;
						default:
							never(result.result);
					}
				},
				retryText: localize("_Authenticate")
			};
		},
		[
			authenticationState,
			integrationName,
			isAuthenticating,
			reload,
			authenticate
		]
	);

	const loadingState: RemoteDataSourceState = getLoadingState();

	const renderContent = (): ReactElement => {
		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={72}
					itemKey={getClientId}
					ref={listHandle}
					trackingId="integration-flow-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={false}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: IntegrationFlowGlobalSearchResultItem, index: number): ReactElement => (
						<IntegrationFlowItemView
							navigationIndex={index}
							item={item}
							index={index}
							itemScope={integrationFlowScope}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		if (isNotNil(authenticationState)) {
			return (
				<>
					<NoResults
						{...authenticateConfiguration}
						trackingId="authentication-required"
						icon="gear"
						height="fill"
						title={localize("_AuthenticationRequired")}
						message={localize("_WeCantRetrieveAnyItemsBecauseYourAreNotAuthenticated")}
					/>
					{isAuthenticating && <BusyIndicator />}
					{authenticationDialogs}
				</>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
IntegrationFlowSearch.displayName = "IntegrationFlowSearch";

type AuthenticationState = {
	missingAuthentications: readonly IntegrationFlowAuthenticationIsRequiredModel[];
	wasInvoked: boolean;
};

export type SpecificIntegrationFlowSearchProps = {
	searchTerm: VersionedValue<string>;
};

export function createIntegrationFlowSearch(integrationId: number, integrationName?: string) : FunctionComponent<SpecificIntegrationFlowSearchProps> {
	const SpecificIntegrationFlowSearch = (props: SpecificIntegrationFlowSearchProps): ReactElement => {
		return (
			<IntegrationFlowSearch
				{...props}
				integrationId={integrationId}
				integrationName={integrationName}
			/>
		);
	};

	SpecificIntegrationFlowSearch.displayName = "SpecificIntegrationFlowSearch";

	return SpecificIntegrationFlowSearch;
}

type ResultItem = ObjectWithClientId<IntegrationFlowGlobalSearchResultItem>;

const PAGE_SIZE = 20;

type FilterState = {
	integrationId: number;
};

const useDataSource = createRemoteDataSource<
	ResultItem,
	FilterState,
	// Trigger new request when searchVersion changes
	{ searchVersion: Guid; }
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true
	})
	.getHook({
		async performRequest(options): Promise<readonly ResultItem[]> {
			const {
				searchTerm,
				filterState,
				signal
			} = options;

			const result: readonly IntegrationFlowGlobalSearchResultItem[] = await searchIntegration(
				filterState.integrationId,
				{
					searchTerm,
					maxAmount: PAGE_SIZE
				},
				signal
			);

			return result
				.map(setClientId);
		},
	});